import React, { useState, useEffect } from 'react'
import AppBar from '../components/AppBar'
import ToggleSwitch from '../components/ToggleSwitch'
import LoadingSpinner from '../components/LoadingSpinner'
import { useNavigate } from 'react-router-dom'
import Gap from '../components/Gap'
import { authApi, userApi } from '../api'
import { toastUtil } from '../utils/toast'
import { dialogUtil } from '../utils/dialog'
import { useAuthStore } from '../stores/authStore'
import { unregisterFcm } from '../utils/fcmRegister'

const AccountManagement: React.FC = () => {
  const navigate = useNavigate()
  const { clearAuth } = useAuthStore()
  const [profilePublic, setProfilePublic] = useState<boolean | null>(null)
  const [isLoadingProfilePublic, setIsLoadingProfilePublic] = useState(true)

  // 프로필 설정 초기값 로드
  useEffect(() => {
    const loadProfileSettings = async () => {
      try {
        const response = await userApi.getProfile()
        if (response.user.is_public !== undefined) {
          setProfilePublic(response.user.is_public)
        } else {
          setProfilePublic(true)
        }
      } catch (error: any) {
        setProfilePublic(true)
      } finally {
        setIsLoadingProfilePublic(false)
      }
    }

    loadProfileSettings()
  }, [])

  // 프로필 공개 설정 변경 핸들러
  const handleProfilePublicChange = async (value: boolean) => {
    if (profilePublic === null) return

    setProfilePublic(value)

    try {
      await userApi.updateProfilePublic(value)
    } catch (error: any) {
      setProfilePublic(!value)
      toastUtil.error('프로필 공개 설정 변경에 실패했습니다')
    }
  }

  const handleLogoutClick = async () => {
    // 인증 토큰이 살아있는 지금(logout/clearAuth 전에) 이 기기 FCM 토큰부터 제거.
    // 내부 try/catch라 실패해도 throw 안 함 → 로그아웃은 항상 진행.
    await unregisterFcm()
    try {
      await authApi.logout()
      clearAuth()
      navigate('/login')
    } catch (error: any) {
      clearAuth()
      toastUtil.error('로그아웃 중 오류가 발생했습니다')
      navigate('/login')
    }
  }

  const handleDeleteAccountClick = async () => {
    const confirmed = await dialogUtil.confirm({
      // 되돌릴 수 없다는 점과, 아이디가 14일 뒤 풀린다는 점을 함께 알린다.
      // 복구 기능이 없어졌으므로 잘못 눌렀을 때 만회할 방법이 없다.
      message:
        '정말 계정을 삭제하시겠습니까?\n삭제된 계정은 복구할 수 없습니다.\n탈퇴 후 14일이 지나면 아이디를 다른 사용자가 사용할 수 있습니다.',
      confirmText: '삭제',
      cancelText: '취소',
    })

    if (!confirmed) {
      return
    }

    await unregisterFcm()
    try {
      await userApi.deleteAccount()
      clearAuth()
      toastUtil.success('계정이 삭제되었습니다')
      navigate('/login')
    } catch (error: any) {
      clearAuth()
      toastUtil.error('계정 삭제 중 오류가 발생했습니다')
      navigate('/login')
    }
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <AppBar title="계정 관리" isBottomBorder />

      <div className="app-container flex-1 overflow-y-auto scrollbar-hide">
        <Gap height={8} />

        {/* 프로필 공개 설정 */}
        <div
          className={`
            w-full px-20px py-16px flex items-center justify-between
            ${isLoadingProfilePublic ? 'cursor-default' : 'cursor-pointer'}
          `}
          onClick={() =>
            !isLoadingProfilePublic &&
            profilePublic !== null &&
            handleProfilePublicChange(!profilePublic)
          }
        >
          <span className="flex-1 font-pretendard font-bold text-16px leading-180% text-brown-80%-main">
            프로필 공개 설정
          </span>
          <Gap width={8} />
          {isLoadingProfilePublic ? (
            <div className="w-50px h-30px flex items-center justify-center shrink-0">
              <LoadingSpinner size={28} borderWidth={2} />
            </div>
          ) : (
            profilePublic !== null && (
              <ToggleSwitch
                checked={profilePublic}
                onChange={handleProfilePublicChange}
              />
            )
          )}
        </div>

        {/*
          차단한 사용자.

          환경설정 첫 화면에 있다가 2026-10-17 여기로 들어왔다. 차단은
          '이 계정이 누구를 안 보는가'라서 계정에 딸린 설정이고, 바깥에
          두면 공지사항·이용약관 같은 **읽을거리 사이에 설정 하나가 섞인다.**

          로그아웃 위에 둔다 — 아래 둘은 화면을 떠나는 동작이라 늘 맨 끝이다.
        */}
        <div
          className="w-full px-20px py-16px flex items-center justify-between cursor-pointer"
          onClick={() => navigate('/blocked-users')}
        >
          <span className="flex-1 font-pretendard font-bold text-16px leading-180% text-brown-80%-main">
            차단한 사용자
          </span>
          <Gap width={8} />
          <img
            src="/images/bold-arrow-right-icon.png"
            alt="이동"
            className="w-24px h-24px object-contain"
          />
        </div>

        {/* 로그아웃 */}
        <div
          className="w-full px-20px py-16px flex items-center justify-between cursor-pointer"
          onClick={handleLogoutClick}
        >
          <span className="flex-1 font-pretendard font-bold text-16px leading-180% text-brown-80%-main">
            로그아웃
          </span>
        </div>

        {/* 계정 삭제하기 */}
        <div
          className="w-full px-20px py-16px flex items-center justify-between cursor-pointer"
          onClick={handleDeleteAccountClick}
        >
          <span className="flex-1 font-pretendard font-bold text-16px leading-180% text-brown-80%-main">
            계정 삭제
          </span>
        </div>
      </div>
    </div>
  )
}

export default AccountManagement
